import { ArenaProfile } from 'shared/contexts/ArenaProfileContext';
import { addDays, weekdayOf } from 'modules/arena/reports/model/dates';
import { CatalogOption, IsoDate } from 'modules/arena/reports/model/types';

// "Banco" de DEMONSTRAÇÃO da área de relatórios. Faz o papel do backend: gera registros
// determinísticos por arena (mesmos números a cada visita) a partir do perfil cadastrado.
// Nada aqui é usado quando a integração real estiver conectada.

export const DEMO_TIMEZONE = 'America/Fortaleza';
export const DEMO_MIN_DATE: IsoDate = '2025-01-01';
export const MIN_RATING_SAMPLE = 5;

// ---------- aleatoriedade determinística ----------

function hash(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function rng(seed: string): () => number {
  let a = hash(seed);
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(random: () => number, items: T[]): T {
  return items[Math.floor(random() * items.length)];
}

function between(random: () => number, min: number, max: number): number {
  return Math.floor(min + random() * (max - min + 1));
}

export function eachDate(start: IsoDate, end: IsoDate): IsoDate[] {
  const dates: IsoDate[] = [];
  for (let date = start; date <= end; date = addDays(date, 1)) dates.push(date);
  return dates;
}

// ---------- entidades ----------

export type Product = 'reserva' | 'aula-avulsa' | 'pacote' | 'torneio';
export type PaymentMethod = 'pix' | 'cartao';
export type PaymentStatus = 'pago' | 'parcial' | 'pendente' | 'vencido' | 'cancelado' | 'reembolso-solicitado' | 'reembolsado';

export interface Order {
  id: string;
  unitId: string;
  sport: string;
  product: Product;
  method: PaymentMethod;
  status: PaymentStatus;
  saleDate: IsoDate;
  dueDate: IsoDate;
  paidDate: IsoDate | null;
  payoutDate: IsoDate | null;
  grossCents: number;
  discountCents: number;
  platformFeeCents: number;
  processorFeeCents: number;
  /** Taxa do processador paga pelo cliente (taxa de serviço): não sai do valor da arena. */
  processorFeePaidByClient: boolean;
  paidCents: number;
  refundedCents: number;
  description: string;
}

export interface ClassGroup {
  id: string;
  label: string;
  sport: string;
  level: string;
  instructorId: string;
  unitId: string;
  weekdays: number[];
  hour: number;
  capacity: number;
  /** Fila de espera registrada? Se não, a espera é "Indisponível" (não zero). */
  tracksWaitlist: boolean;
}

export type SessionStatus = 'realizada' | 'cancelada' | 'remarcada' | 'agendada';

export interface ClassSession {
  id: string;
  groupId: string;
  date: IsoDate;
  status: SessionStatus;
  capacity: number;
  confirmed: number;
  present: number | null;
  absent: number | null;
  waitlist: number | null;
  studentIds: string[];
}

export interface Rating {
  id: string;
  target: 'professor' | 'estrutura';
  instructorId: string | null;
  date: IsoDate;
  score: number;
  studentId: string;
  studentName: string;
  comment: string | null;
}

export interface Plan {
  id: string;
  label: string;
  kind: 'turma-fixa' | 'flexivel';
  /** Exatamente como contratado: "2 aulas por semana" ou "8 aulas no mês". */
  frequencyLabel: string;
  perWeek: number | null;
  perMonth: number | null;
  priceCents: number;
}

export type ContractStatus = 'ativo' | 'renovacao-pendente' | 'vencido' | 'cancelado';

export interface Contract {
  id: string;
  studentId: string;
  studentName: string;
  planId: string;
  groupId: string | null;
  sport: string;
  unitId: string;
  start: IsoDate;
  end: IsoDate;
  status: ContractStatus;
  paymentPending: boolean;
  renewedFrom: string | null;
}

export interface Court {
  id: string;
  label: string;
  unitId: string;
  sports: string[];
}

export interface Tournament {
  id: string;
  label: string;
  sport: string;
  unitId: string;
  date: IsoDate;
  capacityUnit: 'dupla' | 'jogador';
  feeCents: number;
  categories: { id: string; label: string; capacity: number }[];
}

export type RegistrationStatus = 'confirmada' | 'pendente' | 'cancelada' | 'lista-espera';

export interface Registration {
  id: string;
  tournamentId: string;
  categoryId: string;
  team: string;
  status: RegistrationStatus;
  paymentMode: 'integral' | 'dividido';
  dueCents: number;
  paidCents: number;
  registeredAt: IsoDate;
}

export interface DemoDb {
  arenaKey: string;
  arenaName: string;
  units: CatalogOption[];
  sports: string[];
  courts: Court[];
  instructors: (CatalogOption & { sports: string[] })[];
  groups: ClassGroup[];
  plans: Plan[];
  tournaments: Tournament[];
  orders: Order[];
  sessions: ClassSession[];
  ratings: Rating[];
  contracts: Contract[];
  registrations: Registration[];
  openDays: number[];
  openHour: number;
  closeHour: number;
}

const DAY_INDEX: Record<string, number> = { Dom: 0, Seg: 1, Ter: 2, Qua: 3, Qui: 4, Sex: 5, Sáb: 6 };

const STUDENT_NAMES = [
  'Rafael Nunes', 'Marina Leal', 'Ana Paula', 'Léo Souza', 'Bia Ramos', 'Diego Costa', 'Júlia Melo', 'Carla Matos',
  'Bruno Teixeira', 'Paula Klein', 'Igor Freitas', 'Renata Vidal', 'Thiago Barros', 'Fernanda Oliveira', 'Caio Rocha',
  'Marcos Pinto', 'Sofia Nogueira', 'Gabriel Dias', 'Laura Farias', 'Vitor Hugo', 'Camille Soares', 'Pedro Alves',
  'Helena Brito', 'Otávio Lima', 'Lívia Castro', 'Mateus Reis', 'Clara Moura', 'Davi Lopes', 'Alice Prado', 'Enzo Tavares',
];

const BASE_INSTRUCTORS = [
  { id: 'camila', label: 'Camila Rocha', sports: ['Beach Tennis'] },
  { id: 'rui', label: 'Rui Almeida', sports: ['Futevôlei', 'Vôlei de Praia'] },
  { id: 'yuri', label: 'Yuri Santos', sports: ['Pickleball', 'Tênis'] },
  { id: 'marina', label: 'Marina Lopes', sports: ['Beach Tennis'] },
  { id: 'lucas', label: 'Lucas Prado', sports: ['Futevôlei'] },
];

const BASE_GROUPS: Omit<ClassGroup, 'unitId'>[] = [
  { id: 'turma-a', label: 'Turma A · Beach Tennis', sport: 'Beach Tennis', level: 'Iniciante', instructorId: 'camila', weekdays: [1, 3], hour: 7, capacity: 6, tracksWaitlist: true },
  { id: 'turma-b', label: 'Turma B · Beach Tennis', sport: 'Beach Tennis', level: 'Intermediário', instructorId: 'camila', weekdays: [2, 4], hour: 18, capacity: 8, tracksWaitlist: true },
  { id: 'bt-kids', label: 'Beach Tennis Kids', sport: 'Beach Tennis', level: 'Iniciante', instructorId: 'marina', weekdays: [6], hour: 9, capacity: 10, tracksWaitlist: false },
  { id: 'fut-ini', label: 'Futevôlei Iniciantes', sport: 'Futevôlei', level: 'Iniciante', instructorId: 'rui', weekdays: [1, 3], hour: 18, capacity: 8, tracksWaitlist: true },
  { id: 'fut-av', label: 'Futevôlei Avançado', sport: 'Futevôlei', level: 'Avançado', instructorId: 'lucas', weekdays: [5], hour: 19, capacity: 6, tracksWaitlist: true },
  { id: 'volei-mista', label: 'Vôlei de Praia Mista', sport: 'Vôlei de Praia', level: 'Intermediário', instructorId: 'rui', weekdays: [6], hour: 8, capacity: 10, tracksWaitlist: false },
  { id: 'pickle-av', label: 'Pickleball Avançado', sport: 'Pickleball', level: 'Avançado', instructorId: 'yuri', weekdays: [5], hour: 19, capacity: 4, tracksWaitlist: true },
  { id: 'pickle-clinica', label: 'Clínica de Pickleball', sport: 'Pickleball', level: 'Iniciante', instructorId: 'yuri', weekdays: [6], hour: 10, capacity: 20, tracksWaitlist: true },
];

const PLANS: Plan[] = [
  { id: 'p1', label: 'Mensal 1x', kind: 'turma-fixa', frequencyLabel: '1 aula por semana', perWeek: 1, perMonth: null, priceCents: 15000 },
  { id: 'p2', label: 'Mensal 2x', kind: 'turma-fixa', frequencyLabel: '2 aulas por semana', perWeek: 2, perMonth: null, priceCents: 25000 },
  { id: 'p3', label: 'Mensal 3x', kind: 'turma-fixa', frequencyLabel: '3 aulas por semana', perWeek: 3, perMonth: null, priceCents: 34000 },
  { id: 'flex', label: 'Acesso flexível', kind: 'flexivel', frequencyLabel: '8 aulas no mês, em qualquer turma', perWeek: null, perMonth: 8, priceCents: 28000 },
];

function buildTournaments(sports: string[], units: CatalogOption[], today: IsoDate): Tournament[] {
  const base: Omit<Tournament, 'unitId' | 'date'>[] = [
    {
      id: 'open',
      label: 'Open de Beach Tennis',
      sport: 'Beach Tennis',
      capacityUnit: 'dupla',
      feeCents: 18000,
      categories: [
        { id: 'open-mista-c', label: 'Mista C', capacity: 8 },
        { id: 'open-masc-b', label: 'Masculina B', capacity: 8 },
        { id: 'open-fem', label: 'Feminina Open', capacity: 8 },
      ],
    },
    {
      id: 'copa-pickle',
      label: 'Copa de Pickleball',
      sport: 'Pickleball',
      capacityUnit: 'jogador',
      feeCents: 9000,
      categories: [{ id: 'pickle-open', label: 'Simples Open', capacity: 16 }],
    },
    {
      id: 'inverno-fut',
      label: 'Torneio de Inverno de Futevôlei',
      sport: 'Futevôlei',
      capacityUnit: 'dupla',
      feeCents: 16000,
      categories: [{ id: 'fut-unica', label: 'Categoria única', capacity: 16 }],
    },
  ];
  return base
    .filter((tournament) => sports.includes(tournament.sport))
    .map((tournament, index) => ({ ...tournament, unitId: units[index % units.length].id, date: addDays(today, [-4, 12, -40][index] ?? 20) }));
}

const cache = new Map<string, DemoDb>();

/** Banco de demonstração da arena (gerado uma vez por arena e por dia). */
export function getDemoDb(profile: ArenaProfile, isCustom: boolean, today: IsoDate): DemoDb {
  const arenaKey = `${isCustom ? 'custom' : 'default'}:${profile.arenaName}`;
  const cacheKey = `${arenaKey}|${today}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const units: CatalogOption[] = isCustom
    ? [{ id: 'principal', label: profile.unitName }]
    : [
        { id: 'aldeota', label: 'Unidade Aldeota' },
        { id: 'beira-mar', label: 'Unidade Beira Mar' },
      ];
  const sports = profile.sports.length ? profile.sports : ['Beach Tennis'];

  const courts: Court[] = profile.courts.map((court, index) => ({
    id: court.id,
    label: court.name,
    unitId: units[index % units.length].id,
    sports: court.sportIds.length ? court.sportIds : [sports[0]],
  }));

  let instructors = BASE_INSTRUCTORS.filter((item) => item.sports.some((sport) => sports.includes(sport)));
  if (!instructors.length) instructors = [{ ...BASE_INSTRUCTORS[0], sports: [sports[0]] }];
  instructors = instructors.map((item) => ({ ...item, sports: item.sports.filter((sport) => sports.includes(sport)).length ? item.sports.filter((sport) => sports.includes(sport)) : [sports[0]] }));

  const groups: ClassGroup[] = BASE_GROUPS.filter(
    (group) => sports.includes(group.sport) && instructors.some((item) => item.id === group.instructorId)
  ).map((group, index) => ({ ...group, unitId: units[index % units.length].id }));
  if (!groups.length) {
    groups.push({ ...BASE_GROUPS[0], sport: sports[0], label: `Turma A · ${sports[0]}`, instructorId: instructors[0].id, unitId: units[0].id });
  }

  const openDays = (profile.openDays.length ? profile.openDays : ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']).map((day) => DAY_INDEX[day]);
  const openHour = Number(profile.openTime.slice(0, 2)) || 6;
  const closeHour = Number(profile.closeTime.slice(0, 2)) || 22;
  const tournaments = buildTournaments(sports, units, today);

  const db: DemoDb = {
    arenaKey,
    arenaName: profile.arenaName,
    units,
    sports,
    courts,
    instructors,
    groups,
    plans: PLANS,
    tournaments,
    orders: [],
    sessions: [],
    ratings: [],
    contracts: [],
    registrations: [],
    openDays,
    openHour,
    closeHour,
  };

  generateOrders(db, today);
  generateSessions(db, today);
  generateRatings(db, today);
  generateContracts(db, today);
  generateRegistrations(db, today);
  cache.set(cacheKey, db);
  return db;
}

function generateOrders(db: DemoDb, today: IsoDate) {
  const priceByCourt = db.courts.map((court) => ({ court, price: 6000 + (hash(court.id) % 7) * 1000 }));
  let counter = 10000;
  for (const date of eachDate(DEMO_MIN_DATE, today)) {
    const weekday = weekdayOf(date);
    for (const unit of db.units) {
      const random = rng(`${db.arenaKey}|orders|${unit.id}|${date}`);
      const growth = 0.7 + 0.3 * Math.min(1, (Date.parse(date) - Date.parse(DEMO_MIN_DATE)) / (Date.parse(today) - Date.parse(DEMO_MIN_DATE) || 1));
      const base = (weekday === 0 || weekday === 6 ? 13 : 8) * growth * (db.units.length === 1 ? 1.4 : 1);
      const count = Math.max(0, Math.round(base + random() * 4 - 2));
      for (let i = 0; i < count; i++) {
        const roll = random();
        const product: Product =
          roll < 0.52 ? 'reserva' : roll < 0.66 ? 'aula-avulsa' : roll < (db.tournaments.length ? 0.9 : 1) ? 'pacote' : 'torneio';
        const courtsHere = priceByCourt.filter((item) => item.court.unitId === unit.id);
        const courtEntry = courtsHere.length ? pick(random, courtsHere) : priceByCourt[0];
        const tournament = product === 'torneio' ? pick(random, db.tournaments) : null;
        const plan = product === 'pacote' ? pick(random, db.plans) : null;
        const sport = tournament?.sport ?? (courtEntry ? pick(random, courtEntry.court.sports) : db.sports[0]);
        const grossCents =
          product === 'reserva'
            ? (courtEntry?.price ?? 8000) * (random() < 0.3 ? 2 : 1)
            : product === 'aula-avulsa'
              ? between(random, 6, 9) * 1000
              : product === 'pacote'
                ? plan!.priceCents
                : tournament!.feeCents;
        const discountCents = random() < 0.1 ? Math.round(grossCents * (0.05 + random() * 0.1)) : 0;
        const charged = grossCents - discountCents;
        const method: PaymentMethod = random() < 0.62 ? 'pix' : 'cartao';
        const processorFeeCents = Math.round(charged * (method === 'pix' ? 0.0099 : 0.0349));
        const processorFeePaidByClient = method === 'cartao' && random() < 0.5;
        const platformFeeCents = Math.round(charged * 0.02);
        const dueDate = product === 'pacote' || product === 'torneio' ? addDays(date, between(random, 0, 7)) : date;

        const statusRoll = random();
        let status: PaymentStatus;
        let paidDate: IsoDate | null = null;
        let paidCents = 0;
        let refundedCents = 0;
        if (statusRoll < 0.04) status = 'cancelado';
        else if (statusRoll < 0.06) {
          status = 'reembolso-solicitado';
          paidCents = charged;
        } else if (statusRoll < 0.09) {
          status = 'reembolsado';
          paidCents = charged;
          refundedCents = random() < 0.5 ? charged : Math.round(charged / 2);
        } else if (statusRoll < 0.14) {
          status = 'parcial';
          paidCents = Math.round(charged / 2);
        } else if (statusRoll < 0.18 && dueDate < today) status = 'vencido';
        else if (dueDate > today && random() < 0.5) status = 'pendente';
        else {
          status = 'pago';
          paidCents = charged;
        }
        if (paidCents > 0) {
          paidDate = addDays(date, between(random, 0, 2));
          if (paidDate > today) paidDate = today;
        }
        const payoutDate = paidDate && status !== 'parcial' ? addDays(paidDate, 2) : null;

        counter += 1;
        db.orders.push({
          id: `PED-${counter}`,
          unitId: unit.id,
          sport,
          product,
          method,
          status,
          saleDate: date,
          dueDate,
          paidDate,
          payoutDate: payoutDate && payoutDate <= today ? payoutDate : null,
          grossCents,
          discountCents,
          platformFeeCents,
          processorFeeCents,
          processorFeePaidByClient,
          paidCents,
          refundedCents,
          description:
            product === 'reserva'
              ? `Reserva · ${courtEntry?.court.label ?? 'Quadra'}`
              : product === 'aula-avulsa'
                ? `Aula avulsa · ${sport}`
                : product === 'pacote'
                  ? `Pacote ${plan!.label}`
                  : `Inscrição · ${tournament!.label}`,
        });
      }
    }
  }
}

function generateSessions(db: DemoDb, today: IsoDate) {
  const horizon = addDays(today, 14);
  for (const group of db.groups) {
    const roster = Array.from({ length: group.capacity + 4 }, (_, index) => `${group.id}-s${index}`);
    for (const date of eachDate(DEMO_MIN_DATE, horizon)) {
      if (!group.weekdays.includes(weekdayOf(date))) continue;
      const random = rng(`${db.arenaKey}|session|${group.id}|${date}`);
      const future = date > today;
      const roll = random();
      const status: SessionStatus = future ? 'agendada' : roll < 0.05 ? 'cancelada' : roll < 0.08 ? 'remarcada' : 'realizada';
      const demand = group.id === 'pickle-av' ? 0.97 : group.id === 'pickle-clinica' ? 0.88 : 0.62 + random() * 0.35;
      const confirmed = Math.min(group.capacity, Math.round(group.capacity * demand));
      const absent = status === 'realizada' ? Math.min(confirmed, Math.floor(random() * 3)) : null;
      const present = status === 'realizada' ? confirmed - (absent ?? 0) : null;
      const waitlist = group.tracksWaitlist ? (confirmed >= group.capacity ? between(random, 1, 5) : 0) : null;
      db.sessions.push({
        id: `${group.id}|${date}`,
        groupId: group.id,
        date,
        status,
        capacity: group.capacity,
        confirmed,
        present,
        absent,
        waitlist,
        studentIds: status === 'realizada' ? roster.slice(0, present ?? 0) : [],
      });
    }
  }
}

const COMMENTS_PROFESSOR = [
  'Aula muito bem explicada, corrige postura com paciência.',
  'Ótima energia, a turma evolui rápido.',
  'Pontual e atencioso com quem está começando.',
  'Treino puxado na medida certa.',
  null,
  null,
];
const COMMENTS_STRUCTURE = ['Areia bem cuidada.', 'Faltou água no bebedouro.', 'Iluminação ótima à noite.', null, null];

function generateRatings(db: DemoDb, today: IsoDate) {
  const plan: Record<string, { count: number; mean: number }> = {
    camila: { count: 32, mean: 4.8 },
    rui: { count: 18, mean: 4.5 },
    yuri: { count: 9, mean: 4.9 },
    marina: { count: 0, mean: 0 },
    lucas: { count: 3, mean: 4.3 },
  };
  for (const instructor of db.instructors) {
    const config = plan[instructor.id] ?? { count: 6, mean: 4.4 };
    const random = rng(`${db.arenaKey}|ratings|${instructor.id}`);
    for (let i = 0; i < config.count; i++) {
      const student = between(random, 0, STUDENT_NAMES.length - 1);
      db.ratings.push({
        id: `${instructor.id}-r${i}`,
        target: 'professor',
        instructorId: instructor.id,
        date: addDays(today, -between(random, 0, 360)),
        score: Math.max(1, Math.min(5, Math.round(config.mean + (random() - 0.5) * 1.4))),
        studentId: `aluno-${student}`,
        studentName: STUDENT_NAMES[student],
        comment: pick(random, COMMENTS_PROFESSOR),
      });
    }
  }
  const random = rng(`${db.arenaKey}|ratings|estrutura`);
  for (let i = 0; i < 40; i++) {
    const student = between(random, 0, STUDENT_NAMES.length - 1);
    db.ratings.push({
      id: `estrutura-r${i}`,
      target: 'estrutura',
      instructorId: null,
      date: addDays(today, -between(random, 0, 360)),
      score: between(random, 3, 5),
      studentId: `aluno-${student}`,
      studentName: STUDENT_NAMES[student],
      comment: pick(random, COMMENTS_STRUCTURE),
    });
  }
}

function generateContracts(db: DemoDb, today: IsoDate) {
  const random = rng(`${db.arenaKey}|contracts`);
  let counter = 500;
  STUDENT_NAMES.forEach((name, index) => {
    const plan = db.plans[index % db.plans.length];
    const group = plan.kind === 'turma-fixa' ? db.groups[index % db.groups.length] : null;
    let start = addDays(today, -between(random, 20, 400));
    let previous: string | null = null;
    // Cada aluno tem uma sequência de contratos mensais; o último define a situação atual.
    while (start <= today) {
      const end = addDays(start, 29);
      counter += 1;
      const id = `CTR-${counter}`;
      const isCurrent = end >= today;
      const cancelled = !isCurrent && random() < 0.05;
      const stops = !isCurrent && random() < 0.08;
      let status: ContractStatus = 'ativo';
      if (cancelled) status = 'cancelado';
      else if (isCurrent && addDays(today, 7) >= end) status = 'renovacao-pendente';
      else if (!isCurrent) status = 'vencido';
      db.contracts.push({
        id,
        studentId: `aluno-${index}`,
        studentName: name,
        planId: plan.id,
        groupId: group?.id ?? null,
        sport: group?.sport ?? db.sports[0],
        unitId: group?.unitId ?? db.units[0].id,
        start,
        end,
        status,
        paymentPending: isCurrent && random() < 0.15,
        renewedFrom: previous,
      });
      if (cancelled || stops) break;
      previous = id;
      start = addDays(end, 1);
    }
  });
}

function generateRegistrations(db: DemoDb, today: IsoDate) {
  for (const tournament of db.tournaments) {
    for (const category of tournament.categories) {
      const random = rng(`${db.arenaKey}|reg|${category.id}`);
      const total = category.capacity + between(random, 0, 4);
      for (let i = 0; i < total; i++) {
        const a = STUDENT_NAMES[between(random, 0, STUDENT_NAMES.length - 1)];
        const b = STUDENT_NAMES[between(random, 0, STUDENT_NAMES.length - 1)];
        const roll = random();
        const status: RegistrationStatus = i >= category.capacity ? 'lista-espera' : roll < 0.08 ? 'cancelada' : roll < 0.2 ? 'pendente' : 'confirmada';
        const paymentMode = tournament.capacityUnit === 'dupla' && random() < 0.6 ? 'dividido' : 'integral';
        const paidCents =
          status === 'confirmada' ? tournament.feeCents : status === 'pendente' && paymentMode === 'dividido' ? tournament.feeCents / 2 : 0;
        const registeredAt = addDays(tournament.date, -between(random, 3, 35));
        db.registrations.push({
          id: `${category.id}-i${i}`,
          tournamentId: tournament.id,
          categoryId: category.id,
          team: tournament.capacityUnit === 'dupla' ? `${a} & ${b}` : a,
          status,
          paymentMode,
          dueCents: tournament.feeCents,
          paidCents,
          registeredAt: registeredAt > today ? today : registeredAt,
        });
      }
    }
  }
}
